import React, { useState, useEffect } from "react";
import { Plus, Search, Edit2, Trash2 } from "lucide-react";
import axiosInstance from "@/axios";
import Modal from "@/pages/Other/Modal";

const AboutInfo = () => {
    const [aboutInfo, setAboutInfo] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState("create");
    const [selectedIds, setSelectedIds] = useState([]);
    const [formData, setFormData] = useState({
        id: "",
        section: "application_step",
        step_no: "",
        title: "",
        description: "",
        name: "",
    });

    useEffect(() => {
        fetchAboutInfo();
    }, []);

    const fetchAboutInfo = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get("/public/about-info");
            setAboutInfo(response.data || []);
        } catch (error) {
            console.error("Failed to fetch about info:", error);
        } finally {
            setLoading(false);
        }
    };

    const filteredData = aboutInfo.filter((item) =>
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const [activeTab, setActiveTab] = useState("application_step");

    // Split data by section
    const applicationSteps = filteredData.filter(item => item.section === "application_step");
    const eligibilityCriteria = filteredData.filter(item => item.section === "eligibility");

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation rules
        if (formData.section === "application_step") {
            if (!formData.title.trim()) {
                alert("Title is required for Application Step");
                return;
            }
            if (!formData.description.trim()) {
                alert("Description is required for Application Step");
                return;
            }
        } else if (formData.section === "eligibility") {
            if (!formData.name.trim()) {
                alert("Name is required for Eligibility");
                return;
            }
        }

        setLoading(true);
        try {
            if (modalType === "create") {
                await axiosInstance.post("/public/about-info", formData);
            } else if (modalType === "edit") {
                await axiosInstance.put(`/public/about-info/${formData.id}`, formData);
            }
            await fetchAboutInfo();
            setShowModal(false);
            resetForm();
        } catch (error) {
            console.error("Error saving about info:", error);
        } finally {
            setLoading(false);
        }
    };


    const handleEdit = (item) => {
        setFormData(item);
        setModalType("edit");
        setShowModal(true);
    };

    const handleDelete = async (ids) => {
        const idsToDelete = Array.isArray(ids) ? ids : [ids];
        if (!window.confirm(`Delete ${idsToDelete.length} record(s)?`)) return;
        setLoading(true);
        try {
            for (let id of idsToDelete) {
                await axiosInstance.delete(`/public/about-info/${id}`);
            }
            setAboutInfo((prev) => prev.filter((item) => !idsToDelete.includes(item.id)));
            setSelectedIds([]);
        } catch (error) {
            console.error("Error deleting:", error);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            id: "",
            section: "application_step",
            step_no: "",
            title: "",
            description: "",
            name: "",
        });
    };

    const openCreateModal = () => {
        resetForm();
        setModalType("create");
        setShowModal(true);
    };

    return (
        <div className="container mx-auto px-5 mt-3">
            <h1 className="text-2xl font-bold mb-4 p-4 bg-[#264b82] text-white rounded-md">
                About Info
            </h1>

            {/* Search + Add */}
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between mb-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-3 text-gray-400 h-5 w-5" />
                    <input
                        type="text"
                        placeholder="Search by Title or Name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl"
                    />
                </div>

                <button
                    onClick={openCreateModal}
                    className="bg-[#264b82] text-white px-6 py-3 rounded-xl flex items-center gap-2"
                >
                    <Plus className="h-5 w-5" /> Add About Info
                </button>
            </div>
            <div className="flex gap-4 mb-4">
                <button
                    className={`px-4 py-2 rounded ${activeTab === "application_step" ? "bg-[#264b82] text-white" : "bg-gray-200"}`}
                    onClick={() => setActiveTab("application_step")}
                >
                    Application Step
                </button>
                <button
                    className={`px-4 py-2 rounded ${activeTab === "eligibility" ? "bg-[#264b82] text-white" : "bg-gray-200"}`}
                    onClick={() => setActiveTab("eligibility")}
                >
                    Eligibility
                </button>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow overflow-hidden">
                {activeTab === "application_step" && (
                    <table className="min-w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2">Select</th>
                                <th className="px-4 py-2">Step No</th>
                                <th className="px-4 py-2">Title</th>
                                <th className="px-4 py-2">Description</th>
                                <th className="px-4 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {applicationSteps.map((item) => (
                                <tr key={item.id} className="border-b">
                                    <td className="px-4 py-2">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(item.id)}
                                            onChange={(e) =>
                                                e.target.checked
                                                    ? setSelectedIds((prev) => [...prev, item.id])
                                                    : setSelectedIds((prev) => prev.filter((id) => id !== item.id))
                                            }
                                        />
                                    </td>
                                    <td className="px-4 py-2">{item.step_no}</td>
                                    <td className="px-4 py-2">{item.title}</td>
                                    <td className="px-4 py-2">{item.description}</td>
                                    <td className="px-4 py-2 flex gap-2">
                                        <button onClick={() => handleEdit(item)} className="p-2 bg-yellow-500 text-white rounded">Edit</button>
                                        <button onClick={() => handleDelete(item.id)} className="p-2 bg-red-500 text-white rounded">Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {activeTab === "eligibility" && (
                    <table className="min-w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2">Select</th>
                                <th className="px-4 py-2">Name</th>
                                <th className="px-4 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {eligibilityCriteria.map((item) => (
                                <tr key={item.id} className="border-b">
                                    <td className="px-4 py-2">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(item.id)}
                                            onChange={(e) =>
                                                e.target.checked
                                                    ? setSelectedIds((prev) => [...prev, item.id])
                                                    : setSelectedIds((prev) => prev.filter((id) => id !== item.id))
                                            }
                                        />
                                    </td>
                                    <td className="px-4 py-2">{item.name}</td>
                                    <td className="px-4 py-2 flex gap-2">
                                        <button onClick={() => handleEdit(item)} className="p-2 bg-yellow-500 text-white rounded">Edit</button>
                                        <button onClick={() => handleDelete(item.id)} className="p-2 bg-red-500 text-white rounded">Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>


            {/* Modal */}
            {showModal && (
                <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
                    <form onSubmit={handleSubmit}>
                        <div className="bg-[#264b82] px-6 py-4 text-white text-lg">
                            {modalType === "create" ? "Add About Info" : "Edit About Info"}
                        </div>
                        <div>
                            <label>Section *</label>
                            <select
                                name="section"
                                value={formData.section}
                                onChange={handleInputChange}
                                className="w-full border p-2 rounded"
                            >
                                <option value="application_step">Application Step</option>
                                <option value="eligibility">Eligibility</option>
                            </select>
                        </div>

                        {/* Step No - Only show for application_step */}
                        {formData.section === "application_step" && (
                            <div>
                                <label>Step No</label>
                                <input
                                    type="text"
                                    name="step_no"
                                    value={formData.step_no}
                                    onChange={handleInputChange}
                                    className="w-full border p-2 rounded"
                                />
                            </div>
                        )}

                        {/* Title - Only for application_step */}
                        {formData.section === "application_step" && (
                            <div>
                                <label>Title *</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    className="w-full border p-2 rounded"
                                />
                            </div>
                        )}

                        {/* Description - Only for application_step */}
                        {formData.section === "application_step" && (
                            <div>
                                <label>Description *</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows={3}
                                    className="w-full border p-2 rounded"
                                />
                            </div>
                        )}

                        {/* Name - Only for eligibility */}
                        {formData.section === "eligibility" && (
                            <div>
                                <label>Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="w-full border p-2 rounded"
                                />
                            </div>
                        )}

                        <div className="flex justify-end gap-3 p-6 border-t">
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 bg-gray-200 rounded"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-6 py-2 bg-[#264b82] text-white rounded"
                            >
                                {loading ? "Saving..." : modalType === "create" ? "Create" : "Update"}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    );
};

export default AboutInfo;
